import { useMemo, useState } from 'react';
import { PieChart } from 'lucide-react';
import { DEFAULT_GROUP_BY, DEFAULT_RANGE_DAYS } from '../constants';
import { useAdminFeatureUsage, useUserFeatureUsage } from '../hooks';
import type { FeatureGroupBy } from '../types';
import { buildRangeParams } from '../utils';
import { getErrorMessage } from '../../docs/utils/docsUtils';
import { FeatureUsageChart } from './FeatureUsageChart';

interface FeatureUsageSectionProps {
  /** Set for a single user; omit for the whole platform (dashboard). */
  userPublicId?: string;
  title?: string;
  /** `bare` drops the card frame when the parent already provides one (modal body). */
  bare?: boolean;
}

const text = {
  load: 'Unable to load feature usage.',
  platformTitle: 'Feature usage',
} as const;

/** One component for both places the chart appears: per-user (modal) and platform-wide (dashboard). */
export const FeatureUsageSection = ({
  userPublicId,
  title,
  bare = false,
}: FeatureUsageSectionProps) => {
  const [rangeDays, setRangeDays] = useState(DEFAULT_RANGE_DAYS);
  const [groupBy, setGroupBy] = useState<FeatureGroupBy>(DEFAULT_GROUP_BY);
  const params = useMemo(() => buildRangeParams(rangeDays, groupBy), [rangeDays, groupBy]);

  const platform = useAdminFeatureUsage(params, !userPublicId);
  const user = useUserFeatureUsage(userPublicId, params);
  const query = userPublicId ? user : platform;
  const heading = title ?? text.platformTitle;

  const chart = (
    <FeatureUsageChart
      report={query.data}
      isLoading={query.isLoading}
      errorMessage={query.isError ? getErrorMessage(query.error, text.load) : undefined}
      rangeDays={rangeDays}
      groupBy={groupBy}
      onRangeChange={setRangeDays}
      onGroupByChange={setGroupBy}
      showUniqueUsers={!userPublicId}
    />
  );

  return (
    <section
      aria-label={heading}
      className={bare ? '' : 'h-full w-full rounded-xl bg-white p-6 shadow-sm dark:bg-slate-800'}
    >
      <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-gray-900 dark:text-slate-100">
        <PieChart size={20} className="text-blue-500" aria-hidden />
        {heading}
      </h3>
      {chart}
    </section>
  );
};
